import { useClerk } from '@clerk/clerk-react';
import { format } from 'date-fns';
import L from 'leaflet';
import type React from 'react';
import { memo, useCallback, useEffect, useId, useRef, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import {
  BellIcon,
  CalendarIcon,
  CameraIcon,
  CheckIcon,
  ChevronRightIcon,
  CloseIcon,
  DiamondIcon,
  DocumentIcon,
  FolderIcon,
  HomeIcon,
  HouseCheckIcon,
  LocationIcon,
  LogoutIcon,
  MegaphoneIcon,
  MenuIcon,
  MessageIcon,
  PlusIcon,
  SearchIcon,
  SettingsIcon,
  StarIcon,
  UploadIcon,
  UserIcon,
  WrenchIcon,
} from './Icons';
// MissingIcons removed - all icons now in Icons.tsx
import { openModal as setActiveModal, toggleSidebar } from './store/index';
import type { RootState } from './store/index';
import {
  Bid,
  Document,
  type Message,
  type Notification,
  type Plumber,
  type Request,
  type Toast,
  type UserRole,
} from './types';

// Utility function for class names
const cn = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter((c): c is string => Boolean(c)).join(' ');
};

// Enhanced Avatar Component
interface AvatarProps {
  src?: string;
  name: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  onClick?: () => void;
}

export const Avatar: React.FC<AvatarProps> = ({ src, name, size = 'md', className, onClick }) => {
  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-16 h-16 text-xl',
  };

  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const baseClasses = cn(
    'rounded-full flex items-center justify-center font-medium transition-all duration-200',
    sizeClasses[size],
    onClick && 'cursor-pointer hover:scale-105',
    className
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick();
    }
  };

  if (src) {
    return (
      <img
        src={src}
        alt={name}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        tabIndex={onClick ? 0 : undefined}
        role={onClick ? 'button' : undefined}
        className={cn(baseClasses, 'object-cover')}
      />
    );
  }

  return (
    <div
      onClick={onClick}
      onKeyDown={handleKeyDown}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? 'button' : undefined}
      className={cn(baseClasses, 'bg-primary-100 text-primary-700')}
    >
      {initials}
    </div>
  );
};

// Enhanced Status Badge Component
interface StatusBadgeProps {
  status:
    | 'pending'
    | 'completed'
    | 'overdue'
    | 'failed'
    | 'approved'
    | 'denied'
    | 'urgent'
    | 'paid'
    | 'new'
    | 'recurring'
    | 'in_progress'
    | 'resolved'
    | 'rejected';
  size?: 'sm' | 'md';
  className?: string;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, size = 'md', className }) => {
  const statusStyles = {
    pending: 'bg-status-pending text-status-pending-text',
    completed: 'bg-status-completed text-status-completed-text',
    overdue: 'bg-status-overdue text-status-overdue-text',
    failed: 'bg-status-failed text-status-failed-text',
    approved: 'bg-status-approved text-status-approved-text',
    denied: 'bg-status-denied text-status-denied-text',
    urgent: 'bg-status-urgent text-status-urgent-text',
    paid: 'bg-status-completed text-status-completed-text',
    new: 'bg-status-new text-status-new-text',
    recurring: 'bg-status-recurring text-status-recurring-text',
    in_progress: 'bg-primary-100 text-primary-700',
    resolved: 'bg-status-completed text-status-completed-text',
    rejected: 'bg-status-denied text-status-denied-text',
  };

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full',
        statusStyles[status],
        sizeClasses[size],
        className
      )}
    >
      {status.replace('_', ' ').toUpperCase()}
    </span>
  );
};

// Enhanced Card Component
interface CardProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  hover?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

export const Card: React.FC<CardProps> = ({
  children,
  className,
  onClick,
  hover = false,
  padding = 'none',
}) => {
  const Component = onClick ? 'button' : 'div';

  const paddingClasses = {
    none: '',
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6',
  };

  return (
    <Component
      onClick={onClick}
      type={onClick ? 'button' : undefined}
      className={cn(
        'bg-white rounded-2xl shadow-soft overflow-hidden transition-all duration-200',
        hover && 'hover:shadow-medium',
        onClick && 'cursor-pointer w-full text-left hover:scale-[1.02]',
        paddingClasses[padding],
        className
      )}
    >
      {children}
    </Component>
  );
};

// Enhanced Button Component
interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  icon?: React.ReactNode;
  type?: 'button' | 'submit' | 'reset';
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  children,
  onClick,
  disabled,
  className,
  icon,
  type = 'button',
}) => {
  const baseClasses =
    'inline-flex items-center justify-center font-medium rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500';

  const variantClasses = {
    primary:
      'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 shadow-sm hover:shadow-md',
    secondary: 'bg-gray-100 text-gray-900 hover:bg-gray-200 active:bg-gray-300',
    outline: 'border-2 border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50',
    ghost: 'text-gray-700 hover:bg-gray-100 active:bg-gray-200',
    danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800',
  };

  const sizeClasses = {
    xs: 'px-2 py-1 text-xs gap-1',
    sm: 'px-3 py-1.5 text-sm gap-1.5',
    md: 'px-5 py-2.5 text-base gap-2',
    lg: 'px-6 py-3 text-lg gap-2.5',
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        fullWidth && 'w-full',
        className
      )}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {children}
    </button>
  );
};

// Icon Button Component
interface IconButtonProps {
  icon: React.ReactNode;
  onClick?: () => void;
  badge?: number;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
}

export const IconButton: React.FC<IconButtonProps> = ({
  icon,
  onClick,
  badge,
  variant = 'ghost',
  size = 'md',
  disabled,
  className,
  type = 'button',
}) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
  };

  const variantClasses = {
    primary: 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500',
    secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500',
    ghost: 'text-gray-600 hover:bg-gray-100 focus:ring-gray-500',
  };

  return (
    <div className="relative">
      <button
        type={type}
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'relative inline-flex items-center justify-center rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2',
          sizeClasses[size],
          variantClasses[variant],
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
      >
        {icon}
      </button>
      {badge !== undefined && badge > 0 && (
        <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs font-medium rounded-full flex items-center justify-center">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </div>
  );
};

// Toggle Switch Component
interface ToggleSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
  size?: 'sm' | 'md';
}

export const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  checked,
  onChange,
  disabled,
  label,
  size = 'md',
}) => {
  const id = useId();
  const sizeClasses = {
    sm: { container: 'h-5 w-9', thumb: 'h-3 w-3', translate: 'translate-x-5' },
    md: { container: 'h-6 w-11', thumb: 'h-4 w-4', translate: 'translate-x-6' },
  };

  return (
    <div className="flex items-center">
      {label && (
        <label htmlFor={id} className="mr-3 text-gray-700 cursor-pointer">
          {label}
        </label>
      )}
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500',
          sizeClasses[size].container,
          checked ? 'bg-primary-600' : 'bg-gray-300',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <span
          className={cn(
            'inline-block transform rounded-full bg-white transition-transform duration-200 shadow-sm',
            sizeClasses[size].thumb,
            checked ? sizeClasses[size].translate : 'translate-x-1'
          )}
        />
      </button>
    </div>
  );
};

// List Item Component
interface ListItemProps {
  avatar?: React.ReactNode;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  onClick?: () => void;
  className?: string;
  badge?: React.ReactNode;
}

export const ListItem: React.FC<ListItemProps> = ({
  avatar,
  title,
  subtitle,
  action,
  onClick,
  className,
  badge,
}) => {
  const Component = onClick ? 'button' : 'div';

  return (
    <Component
      onClick={onClick}
      type={onClick ? 'button' : undefined}
      className={cn(
        'flex items-center gap-3 p-4 w-full transition-colors duration-200',
        onClick && 'hover:bg-gray-50 cursor-pointer',
        className
      )}
    >
      {avatar && <div className="flex-shrink-0">{avatar}</div>}
      <div className="flex-1 min-w-0 text-left">
        <div className="flex items-center gap-2">
          <p className="font-medium text-gray-900 truncate">{title}</p>
          {badge}
        </div>
        {subtitle && <p className="text-sm text-gray-500 truncate">{subtitle}</p>}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </Component>
  );
};

// Tab Navigation Component
interface Tab {
  id: string;
  label: string;
  count?: number;
  icon?: React.ReactNode;
}

interface TabNavigationProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  className?: string;
  variant?: 'pills' | 'underline';
}

export const TabNavigation: React.FC<TabNavigationProps> = ({
  tabs,
  activeTab,
  onTabChange,
  className,
  variant = 'pills',
}) => {
  if (variant === 'underline') {
    return (
      <div className={cn('flex border-b border-gray-200', className)}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => onTabChange(tab.id)}
            className={cn(
              'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors duration-200',
              activeTab === tab.id
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            )}
          >
            {tab.icon}
            {tab.label}
            {tab.count !== undefined && (
              <span className="text-xs text-gray-400">({tab.count})</span>
            )}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className={cn('flex space-x-1 p-1 bg-gray-100 rounded-xl', className)}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onTabChange(tab.id)}
          className={cn(
            'flex-1 flex items-center justify-center gap-2 py-2 px-3 text-sm font-medium rounded-lg transition-all duration-200',
            activeTab === tab.id
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          )}
        >
          {tab.icon}
          {tab.label}
          {tab.count !== undefined && <span className="text-xs text-gray-500">({tab.count})</span>}
        </button>
      ))}
    </div>
  );
};

// Enhanced Input Component
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  icon?: React.ReactNode;
  helper?: string;
}

export const Input: React.FC<InputProps> = ({
  label,
  error,
  icon,
  helper,
  className,
  value,
  onChange,
  id,
  ...props
}) => {
  const inputId = id || useId();

  return (
    <div className="space-y-1">
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
            {icon}
          </div>
        )}
        <input
          id={inputId}
          value={value}
          onChange={onChange}
          className={cn(
            'block w-full px-4 py-3 text-gray-900 placeholder-gray-400 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors',
            icon ? 'pl-10' : '',
            error && 'border-red-500 focus:ring-red-500',
            className
          )}
          {...props}
        />
      </div>
      {helper && !error && <p className="text-sm text-gray-500">{helper}</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};

// Enhanced TextArea Component
interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helper?: string;
}

export const TextArea: React.FC<TextAreaProps> = ({
  label,
  error,
  helper,
  className,
  value,
  onChange,
  id,
  ...props
}) => {
  const textareaId = id || useId();

  return (
    <div className="space-y-1">
      {label && (
        <label htmlFor={textareaId} className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <textarea
        id={textareaId}
        value={value}
        onChange={onChange}
        className={cn(
          'block w-full px-4 py-3 text-gray-900 placeholder-gray-400 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-none',
          error && 'border-red-500 focus:ring-red-500',
          className
        )}
        {...props}
      />
      {helper && !error && <p className="text-sm text-gray-500">{helper}</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};

// Enhanced Select Component
interface SelectProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  error?: string;
  className?: string;
  id?: string;
}

export const Select: React.FC<SelectProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  error,
  className,
  id,
}) => {
  const selectId = id || useId();

  return (
    <div className="space-y-1">
      {label && (
        <label htmlFor={selectId} className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <select
        id={selectId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'block w-full px-4 py-3 text-gray-900 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors',
          error && 'border-red-500 focus:ring-red-500',
          className
        )}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};

// Toast Notification Component
interface ToastProps {
  toast: Toast;
  onClose: (id: string) => void;
}

export const ToastNotification: React.FC<ToastProps> = ({ toast, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (toast.duration) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => onClose(toast.id), 300);
      }, toast.duration);
      return () => clearTimeout(timer);
    }
  }, [toast.duration, toast.id, onClose]);

  const typeStyles: Record<Toast['type'], string> = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  const iconMap: Record<Toast['type'], React.ReactElement> = {
    success: <CheckIcon size={20} />,
    error: <CloseIcon size={20} />,
    warning: <span className="text-lg">⚠</span>,
    info: <span className="text-lg">ℹ</span>,
  };

  return (
    <div
      className={cn(
        'flex items-center gap-3 p-4 border rounded-xl shadow-lg transition-all duration-300 max-w-sm',
        typeStyles[toast.type],
        isVisible ? 'animate-slide-up opacity-100' : 'opacity-0 translate-y-2'
      )}
    >
      <div className="flex-shrink-0">{iconMap[toast.type]}</div>
      <div className="flex-1">
        <p className="text-sm font-medium">{toast.message}</p>
      </div>
      <button
        type="button"
        onClick={() => {
          setIsVisible(false);
          setTimeout(() => onClose(toast.id), 300);
        }}
        className="flex-shrink-0 text-gray-400 hover:text-gray-600"
      >
        <CloseIcon size={16} />
      </button>
    </div>
  );
};

// Toast Container Component
interface ToastContainerProps {
  toasts: Toast[];
  onClose: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onClose }) => {
  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {toasts.map((toast) => (
        <ToastNotification key={toast.id} toast={toast} onClose={onClose} />
      ))}
    </div>
  );
};

// Empty State Component
interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
}

export const EmptyState: React.FC<EmptyStateProps> = ({ icon, title, description, action }) => {
  return (
    <div className="text-center py-12 px-4">
      {icon && <div className="mx-auto text-gray-400 mb-4 flex justify-center">{icon}</div>}
      <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
      {description && <p className="text-gray-500 mb-4 max-w-sm mx-auto">{description}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
};

// Loading Spinner Component
interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ size = 'md', className }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  };

  return (
    <div className={cn('flex justify-center', className)}>
      <div
        className={cn(
          'animate-spin rounded-full border-2 border-gray-300 border-t-primary-600',
          sizeClasses[size]
        )}
      />
    </div>
  );
};

// Dashboard Widget Component (existing, enhanced)
interface DashboardWidgetProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color?: 'blue' | 'green' | 'purple' | 'orange' | 'red';
  onClick?: () => void;
  trend?: {
    value: number;
    isPositive: boolean;
  };
}

export const DashboardWidget: React.FC<DashboardWidgetProps> = ({
  title,
  value,
  subtitle,
  icon,
  color = 'blue',
  onClick,
  trend,
}) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
    red: 'bg-red-50 text-red-600',
  };

  return (
    <Card onClick={onClick} hover={!!onClick} padding="lg" className="h-full">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
          {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
          {trend && (
            <div className="flex items-center mt-2">
              <span
                className={cn(
                  'text-sm font-medium',
                  trend.isPositive ? 'text-green-600' : 'text-red-600'
                )}
              >
                {trend.isPositive ? '↑' : '↓'} {Math.abs(trend.value)}%
              </span>
            </div>
          )}
        </div>
        <div
          className={cn(
            'w-12 h-12 rounded-xl flex items-center justify-center',
            colorClasses[color]
          )}
        >
          {icon}
        </div>
      </div>
    </Card>
  );
};

// Request Card Component (existing, enhanced)
interface RequestCardProps {
  request: Request;
  onSelect: () => void;
  showVendor?: boolean;
}

export const RequestCard: React.FC<RequestCardProps> = memo(({ request, onSelect }) => {
  const priorityColors = {
    low: 'text-green-600 bg-green-100',
    medium: 'text-yellow-600 bg-yellow-100',
    high: 'text-orange-600 bg-orange-100',
    urgent: 'text-red-600 bg-red-100',
  };

  return (
    <Card onClick={onSelect} hover padding="lg">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 mb-1">{request.title}</h3>
          <p className="text-sm text-gray-600">{request.location.address}</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusBadge status={request.status} size="sm" />
          <span
            className={cn(
              'px-2 py-1 rounded-full text-xs font-medium',
              priorityColors[request.priority]
            )}
          >
            {request.priority}
          </span>
        </div>
      </div>

      <p className="text-sm text-gray-700 mb-3 line-clamp-2">{request.description}</p>

      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>{new Date(request.createdAt).toLocaleDateString()}</span>
        {request.estimatedCost && (
          <span className="font-medium text-gray-900">
            ${request.estimatedCost.toLocaleString()}
          </span>
        )}
      </div>
    </Card>
  );
});

// Vendor Card Component (existing, enhanced)
interface VendorCardProps {
  vendor: Plumber;
  onSelect: () => void;
  showDistance?: boolean;
}

export const VendorCard: React.FC<VendorCardProps> = memo(({ vendor, onSelect }) => {
  return (
    <Card onClick={onSelect} hover padding="lg">
      <div className="flex items-start gap-4">
        <Avatar name={vendor.name} size="lg" />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="font-semibold text-gray-900">{vendor.name}</h3>
              <p className="text-sm text-gray-600 capitalize">{vendor.category}</p>
            </div>
            {vendor.verified && (
              <div className="flex items-center gap-1 text-primary-600">
                <CheckIcon size={16} />
                <span className="text-xs">Verified</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1 mb-2">
            <StarIcon size={16} className="text-yellow-400" />
            <span className="text-sm font-medium">{vendor.rating}</span>
            <span className="text-sm text-gray-500">({vendor.reviewCount} reviews)</span>
          </div>

          <div className="flex items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-1">
              <LocationIcon size={14} />
              <span>{vendor.location}</span>
            </div>
            {vendor.price && <span className="font-medium text-gray-900">{vendor.price}</span>}
          </div>

          {vendor.specialties && (
            <div className="mt-2 flex flex-wrap gap-1">
              {vendor.specialties.slice(0, 2).map((specialty) => (
                <span
                  key={specialty}
                  className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-lg"
                >
                  {specialty}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
});

// Message Item Component (existing, enhanced)
interface MessageItemProps {
  message: Message;
  onSelect: () => void;
}

export const MessageItem: React.FC<MessageItemProps> = ({ message, onSelect }) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <button
      type="button"
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      className={cn(
        'w-full text-left p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors',
        !message.read && 'bg-blue-50'
      )}
    >
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-medium text-gray-900">{message.subject}</h4>
        <span className="text-xs text-gray-500">
          {new Date(message.createdAt).toLocaleDateString()}
        </span>
      </div>
      <p className="text-sm text-gray-600 line-clamp-2">{message.content}</p>
      {!message.read && <div className="w-2 h-2 bg-primary-600 rounded-full mt-2" />}
    </button>
  );
};

// Notification Item Component (existing, enhanced)
interface NotificationItemProps {
  notification: Notification;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({ notification }) => {
  const typeIcons = {
    request: <WrenchIcon size={20} />,
    message: <MessageIcon size={20} />,
    payment: <span className="text-lg">💳</span>,
    document: <span className="text-lg">📄</span>,
    system: <span className="text-lg">⚙️</span>,
  };

  return (
    <div
      className={cn(
        'p-4 border-b border-gray-100 transition-colors',
        !notification.read && 'bg-blue-50'
      )}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 text-gray-400">{typeIcons[notification.type]}</div>
        <div className="flex-1">
          <h4 className="font-medium text-gray-900 mb-1">{notification.title}</h4>
          <p className="text-sm text-gray-600">{notification.content}</p>
          <span className="text-xs text-gray-500 mt-1">
            {new Date(notification.createdAt).toLocaleDateString()}
          </span>
        </div>
        {!notification.read && <div className="w-2 h-2 bg-primary-600 rounded-full" />}
      </div>
    </div>
  );
};

// Modal Component (existing, enhanced)
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  hideCloseButton?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  hideCloseButton = false,
}) => {
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  const handleBackdropKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
          onKeyDown={handleBackdropKeyDown}
          role="button"
          tabIndex={0}
          aria-label="Close modal"
        />
        <div
          className={cn(
            'relative bg-white rounded-2xl shadow-large w-full animate-scale-in',
            sizeClasses[size]
          )}
          onKeyDown={handleKeyDown}
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
            {!hideCloseButton && (
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <CloseIcon size={24} />
              </button>
            )}
          </div>
          <div className="p-6">{children}</div>
        </div>
      </div>
    </div>
  );
};

// Map View Component (Enhanced with LA Integration)
interface MapViewProps {
  vendors: Plumber[];
  selectedVendor?: string;
  onVendorSelect: (id: string) => void;
  className?: string;
  height?: string;
}

export const MapView: React.FC<MapViewProps> = ({
  vendors,
  selectedVendor,
  onVendorSelect,
  className,
  height = '400px',
}) => {
  // Default map center for Rochester Hills, MI
  const mapCenter = useSelector((state: RootState) => state.ui?.mapCenter || [42.6583, -83.1499]);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  const vendorsWithCoordinates = vendors.filter(
    (vendor) =>
      vendor.coordinates &&
      vendor.coordinates.length === 2 &&
      !Number.isNaN(vendor.coordinates[0]) &&
      !Number.isNaN(vendor.coordinates[1])
  );

  // Enhanced marker icons with different colors based on rating
  const createVendorIcon = (vendor: Plumber, isSelected: boolean) => {
    const rating = vendor.rating || 0;
    let color = '#6B7280'; // gray for unrated

    if (rating >= 4.8)
      color = '#10B981'; // green for excellent
    else if (rating >= 4.5)
      color = '#3B82F6'; // blue for very good
    else if (rating >= 4.0)
      color = '#F59E0B'; // yellow for good
    else if (rating >= 3.5) color = '#EF4444'; // red for poor

    const size = isSelected ? 40 : 32;
    const zIndex = isSelected ? 1000 : 100;

    return L.divIcon({
      html: `
        <div class="relative">
          <div class="w-${size === 40 ? '10' : '8'} h-${size === 40 ? '10' : '8'} 
                      bg-white/95 backdrop-blur-sm rounded-full border-2 
                      border-white shadow-lg flex items-center justify-center
                      ${isSelected ? 'ring-2 ring-primary-500 ring-offset-2' : ''}"
               style="background-color: ${color}; z-index: ${zIndex}">
            <svg width="${size === 40 ? '20' : '16'}" height="${size === 40 ? '20' : '16'}" 
                 viewBox="0 0 24 24" fill="white">
              <path d="M14.7 6.3C15.0523 6.65228 15.0523 7.22515 14.7 7.57743L7.57743 14.7C7.22515 15.0523 6.65228 15.0523 6.3 14.7L3.7 12.1C3.34772 11.7477 3.34772 11.1749 3.7 10.8226L10.8226 3.7C11.1749 3.34772 11.7477 3.34772 12.1 3.7L14.7 6.3Z" 
                    stroke="currentColor" stroke-width="2"/>
            </svg>
          </div>
          ${
            vendor.rating
              ? `
            <div class="absolute -bottom-1 -right-1 bg-white/90 backdrop-blur-sm 
                        text-xs font-bold px-1 rounded text-gray-800 shadow-sm">
              ${vendor.rating.toFixed(1)}
            </div>
          `
              : ''
          }
        </div>
      `,
      className: 'vendor-marker',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  };

  // Error boundary for map
  useEffect(() => {
    try {
      setMapError(null);
      setMapReady(true);
    } catch (error) {
      setMapError('Failed to initialize map');
      console.error('Map initialization error:', error);
    }
  }, []);

  if (mapError) {
    return (
      <div
        className={cn(
          'flex items-center justify-center bg-gray-50/80 backdrop-blur-sm rounded-lg border border-gray-200/50',
          className
        )}
        style={{ height }}
      >
        <div className="text-center p-6">
          <LocationIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">Map temporarily unavailable</p>
          <p className="text-sm text-gray-500 mt-1">Please try refreshing the page</p>
        </div>
      </div>
    );
  }

  if (!mapReady) {
    return (
      <div
        className={cn(
          'flex items-center justify-center bg-gray-50/80 backdrop-blur-sm rounded-lg border border-gray-200/50',
          className
        )}
        style={{ height }}
      >
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative rounded-lg overflow-hidden border border-gray-200/50 shadow-sm',
        'bg-white/80 backdrop-blur-sm',
        className
      )}
      style={{ height }}
    >
      {/* Map Controls Overlay */}
      <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2">
        <div className="bg-white/90 backdrop-blur-sm rounded-lg p-2 shadow-sm border border-gray-200/50">
          <p className="text-xs text-gray-600 font-medium">
            {vendorsWithCoordinates.length} vendors nearby
          </p>
        </div>

        {/* Legend */}
        <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-sm border border-gray-200/50 text-xs">
          <p className="font-medium text-gray-800 mb-2">Rating Legend</p>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500" />
              <span className="text-gray-600">Excellent (4.8+)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-gray-600">Very Good (4.5+)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-500" />
              <span className="text-gray-600">Good (4.0+)</span>
            </div>
          </div>
        </div>
      </div>

      <MapContainer
        center={mapCenter}
        zoom={11}
        zoomControl={false}
        style={{ height: '100%', width: '100%' }}
        className="z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={18}
        />

        {/* Zoom Controls */}
        <div className="leaflet-control-zoom leaflet-bar leaflet-control">
          <button
            type="button"
            className="leaflet-control-zoom-in"
            title="Zoom in"
            aria-label="Zoom in"
            onClick={(e) => {
              e.preventDefault();
              // Zoom functionality will be handled by Leaflet
            }}
          >
            +
          </button>
          <button
            type="button"
            className="leaflet-control-zoom-out"
            title="Zoom out"
            aria-label="Zoom out"
            onClick={(e) => {
              e.preventDefault();
              // Zoom functionality will be handled by Leaflet
            }}
          >
            −
          </button>
        </div>

        {/* HOA Center Marker */}
        <Marker
          position={mapCenter}
          icon={L.divIcon({
            html: `
              <div class="w-12 h-12 bg-primary-600 rounded-full border-4 border-white shadow-lg 
                          flex items-center justify-center animate-pulse">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                  <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
                </svg>
              </div>
            `,
            className: 'hoa-center-marker',
            iconSize: [48, 48],
            iconAnchor: [24, 24],
          })}
        >
          <Popup>
            <div className="p-3 text-center">
              <h3 className="font-semibold text-primary-600">🏠 EZHOA Community</h3>
              <p className="text-sm text-gray-600 mt-1">Los Angeles, CA</p>
              <p className="text-xs text-gray-500 mt-2">Your HOA management center</p>
            </div>
          </Popup>
        </Marker>

        {/* Vendor Markers */}
        {vendorsWithCoordinates.map((vendor) => {
          const isSelected = selectedVendor === vendor.id;
          return (
            <Marker
              key={vendor.id}
              position={vendor.coordinates as [number, number]}
              icon={createVendorIcon(vendor, isSelected)}
              eventHandlers={{
                click: () => onVendorSelect(vendor.id),
              }}
              zIndexOffset={isSelected ? 1000 : 0}
            >
              <Popup maxWidth={280} className="vendor-popup">
                <div className="p-1">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 leading-tight">{vendor.name}</h3>
                      <p className="text-sm text-gray-600 capitalize">
                        {vendor.category || 'Plumber'}
                      </p>

                      <div className="flex items-center gap-2 mt-2">
                        <div className="flex items-center">
                          <StarIcon size={14} className="text-yellow-400" />
                          <span className="text-sm font-medium ml-1">
                            {vendor.rating?.toFixed(1) || 'N/A'}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          ({vendor.reviewCount || 0} reviews)
                        </span>
                      </div>

                      {vendor.hours && (
                        <p className="text-xs text-gray-600 mt-1">🕒 {vendor.hours}</p>
                      )}

                      {vendor.phone && <p className="text-xs text-gray-600">📞 {vendor.phone}</p>}

                      {vendor.testimonial && (
                        <p className="text-xs text-gray-700 italic mt-2 p-2 bg-gray-50 rounded">
                          "{vendor.testimonial}"
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 mt-3">
                    <Button
                      size="xs"
                      variant="primary"
                      className="flex-1 text-xs"
                      onClick={() => onVendorSelect(vendor.id)}
                    >
                      View Details
                    </Button>
                    {vendor.phone && (
                      <Button
                        size="xs"
                        variant="outline"
                        className="text-xs"
                        onClick={() => window.open(`tel:${vendor.phone}`, '_self')}
                      >
                        Call
                      </Button>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Mobile-specific overlay */}
      <div className="absolute bottom-4 left-4 right-4 lg:hidden">
        <div className="bg-white/95 backdrop-blur-sm rounded-lg p-3 shadow-sm border border-gray-200/50">
          <p className="text-sm font-medium text-gray-800">Tap markers to view vendor details</p>
          <p className="text-xs text-gray-600 mt-1">Pinch to zoom • Drag to explore</p>
        </div>
      </div>
    </div>
  );
};

// Quick Actions Component (existing)
interface QuickActionsProps {
  userRole: 'admin' | 'homeowner' | 'vendor';
}

export const QuickActions: React.FC<QuickActionsProps> = ({ userRole }) => {
  const actions = {
    admin: [
      { icon: <UserIcon />, label: 'Manage Users', path: '/users' },
      { icon: <WrenchIcon />, label: 'Vendor Approval', path: '/vendors' },
      { icon: <span className="text-lg">📊</span>, label: 'Analytics', path: '/analytics' },
      { icon: <span className="text-lg">💰</span>, label: 'Finance', path: '/finance' },
    ],
    homeowner: [
      { icon: <PlusIcon />, label: 'New Request', path: '/requests/new' },
      { icon: <MessageIcon />, label: 'Messages', path: '/messages' },
      { icon: <span className="text-lg">📋</span>, label: 'My Requests', path: '/requests' },
      { icon: <span className="text-lg">💳</span>, label: 'Payments', path: '/payments' },
    ],
    vendor: [
      { icon: <SearchIcon />, label: 'Find Jobs', path: '/jobs' },
      { icon: <WrenchIcon />, label: 'My Jobs', path: '/jobs/mine' },
      { icon: <MessageIcon />, label: 'Messages', path: '/messages' },
      { icon: <span className="text-lg">📈</span>, label: 'Earnings', path: '/earnings' },
    ],
  };

  return (
    <Card padding="lg">
      <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
      <div className="grid grid-cols-2 gap-3">
        {actions[userRole].map((action) => (
          <Button
            key={action.path}
            variant="outline"
            size="sm"
            icon={action.icon}
            className="justify-start"
          >
            {action.label}
          </Button>
        ))}
      </div>
    </Card>
  );
};

// Search Bar Component (existing)
interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  placeholder = 'Search...',
  className,
}) => {
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      icon={<SearchIcon size={20} />}
      className={className}
    />
  );
};

// Bottom Navigation Component (Mobile)
interface BottomNavProps {
  userRole: 'admin' | 'homeowner' | 'vendor';
  currentPath: string;
  onNavigate: (path: string) => void;
}

export const BottomNav: React.FC<BottomNavProps> = ({ userRole, currentPath, onNavigate }) => {
  const getNavItems = () => {
    // 5 items for all users as per design
    const baseItems = [
      { path: '/calendar', icon: <CalendarIcon size={20} />, label: 'Calendar' },
      { path: '/documents', icon: <DocumentIcon size={20} />, label: 'Documents' },
      { path: '/dashboard', icon: <HomeIcon size={24} />, label: 'Home', isCenter: true },
      { path: '/requests', icon: <WrenchIcon size={20} />, label: 'Requests' },
      { path: '/settings', icon: <SettingsIcon size={20} />, label: 'Settings' },
    ];

    // Customize based on role if needed
    switch (userRole) {
      case 'admin':
        return baseItems;
      case 'vendor':
        // Replace requests with jobs for vendors
        return baseItems.map((item) =>
          item.path === '/requests'
            ? { path: '/jobs', icon: <WrenchIcon size={20} />, label: 'Jobs' }
            : item
        );
      default: // homeowner
        return baseItems;
    }
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-gray-200/50 lg:hidden z-40">
      <div className="grid grid-cols-5 h-16">
        {getNavItems().map((item) => (
          <button
            key={item.path}
            type="button"
            onClick={() => onNavigate(item.path)}
            className={cn(
              'flex flex-col items-center justify-center gap-1 text-xs font-medium transition-colors duration-200',
              item.isCenter ? 'relative' : '',
              currentPath === item.path ? 'text-blue-600' : 'text-gray-600 hover:text-gray-900'
            )}
          >
            {item.isCenter ? (
              <div className="absolute -top-4 w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
                <HomeIcon size={24} className="text-white" />
              </div>
            ) : (
              <>
                {item.icon}
                <span className={currentPath === item.path ? 'text-blue-600' : ''}>
                  {item.label}
                </span>
              </>
            )}
            {item.isCenter && <span className="mt-6 text-blue-600">{item.label}</span>}
          </button>
        ))}
      </div>
    </nav>
  );
};

// Sidebar Component (Desktop)
interface SidebarProps {
  isOpen: boolean;
  userRole: 'admin' | 'homeowner' | 'vendor';
  currentPath: string;
  onNavigate: (path: string) => void;
  onClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  userRole,
  currentPath,
  onNavigate,
  onClose,
}) => {
  const { signOut } = useClerk();

  const getNavItems = () => {
    const commonItems = [
      { path: '/dashboard', icon: <HomeIcon size={20} />, label: 'Dashboard' },
      { path: '/messages', icon: <MessageIcon size={20} />, label: 'Messages' },
      { path: '/notifications', icon: <BellIcon size={20} />, label: 'Notifications' },
      { path: '/calendar', icon: <CalendarIcon size={20} />, label: 'Calendar' },
    ];

    const roleSpecificItems = {
      admin: [
        { path: '/requests', icon: <WrenchIcon size={20} />, label: 'Service Requests' },
        { path: '/vendors', icon: <UserIcon size={20} />, label: 'Vendor Management' },
        { path: '/vendor-assignment', icon: <CheckIcon size={20} />, label: 'Assign Vendors' },
        { path: '/broadcasts', icon: <MegaphoneIcon size={20} />, label: 'Broadcasts' },
        { path: '/payments', icon: <StarIcon size={20} />, label: 'Payments' },
        { path: '/ai-agent', icon: <DiamondIcon size={20} />, label: 'AI Assistant' },
        { path: '/documents', icon: <DocumentIcon size={20} />, label: 'Documents' },
        { path: '/privacy', icon: <UserIcon size={20} />, label: 'Privacy Settings' },
        { path: '/settings', icon: <SettingsIcon size={20} />, label: 'Settings' },
      ],
      vendor: [
        { path: '/jobs', icon: <WrenchIcon size={20} />, label: 'Available Jobs' },
        { path: '/requests', icon: <CheckIcon size={20} />, label: 'My Jobs' },
        { path: '/profile', icon: <UserIcon size={20} />, label: 'Profile' },
        { path: '/settings', icon: <SettingsIcon size={20} />, label: 'Settings' },
      ],
      homeowner: [
        { path: '/requests', icon: <WrenchIcon size={20} />, label: 'My Requests' },
        { path: '/vendor-marketplace', icon: <UserIcon size={20} />, label: 'Find Vendors' },
        { path: '/payments', icon: <StarIcon size={20} />, label: 'Payments' },
        { path: '/ai-agent', icon: <DiamondIcon size={20} />, label: 'AI Assistant' },
        { path: '/documents', icon: <DocumentIcon size={20} />, label: 'Documents' },
        { path: '/profile', icon: <UserIcon size={20} />, label: 'Profile' },
        { path: '/settings', icon: <SettingsIcon size={20} />, label: 'Settings' },
      ],
    };

    return [...commonItems, ...roleSpecificItems[userRole]];
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => onClose?.()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClose?.();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Close sidebar"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 h-full w-64 bg-white/95 backdrop-blur-md border-r border-gray-200/50 z-40 transition-transform duration-300',
          'lg:translate-x-0 lg:bg-white lg:backdrop-blur-none',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 p-6 border-b border-gray-200/50">
            <HouseCheckIcon size={32} className="text-primary-600" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">EZHOA</h1>
              <p className="text-xs text-gray-500 capitalize">{userRole} Portal</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {getNavItems().map((item) => (
              <button
                key={item.path}
                type="button"
                onClick={() => onNavigate(item.path)}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200',
                  currentPath === item.path
                    ? 'bg-primary-50 text-primary-700 shadow-sm'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                )}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            ))}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200/50 space-y-2">
            <button
              type="button"
              onClick={() => signOut({ redirectUrl: '/' })}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl text-red-600 hover:bg-red-50 transition-all duration-200"
            >
              <LogoutIcon size={20} />
              <span>Sign Out</span>
            </button>

            <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl">
              <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                <HouseCheckIcon size={16} className="text-white" />
              </div>
              <div>
                <p className="text-xs font-medium text-primary-700">EZHOA Pro</p>
                <p className="text-xs text-primary-600">Community Management</p>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};
